import React, { createContext, useContext, useState, useCallback } from 'react';
import CreateUserModal from '@/components/shared/CreateUserModal';
import CreateOrganizationModal from '@/components/shared/CreateOrganizationModal';
import CreateTeamModal from '@/components/shared/CreateTeamModal';
import TeamCreatedModal from '@/components/shared/TeamCreatedModal';
import InviteUserModal from '@/components/modals/InviteUserModal';
import { getApiUrl } from '@/config/api';
import { CreateUserData } from '@/types';

interface TeamCreatedData {
  team: {
    id: number;
    name: string;
    organization: { name: string };
    description?: string;
  };
  coach_code: string;
  athlete_code: string;
}

interface InviteModalOptions {
  defaultRole?: 'athlete' | 'coach';
}

interface ModalContextType {
  openCreateUserModal: () => void;
  openInviteUserModal: (options?: InviteModalOptions) => void;
  openCreateOrgModal: () => void;
  openCreateTeamModal: () => void;
}

const ModalContext = createContext<ModalContextType | null>(null);

export const useModals = (): ModalContextType => {
  const ctx = useContext(ModalContext);
  if (!ctx) throw new Error('useModals must be used within ModalProvider');
  return ctx;
};

/**
 * Custom event dispatched after any create-modal action succeeds.
 * Pages listen for this to refresh their data.
 */
export const MODAL_SUCCESS_EVENT = 'cgm:modal-success';

/**
 * Hook for pages to refresh data after a modal create action succeeds.
 * Uses a ref internally so the listener always calls the latest callback
 * without needing to re-register on every render.
 */
export function useModalSuccess(onSuccess: () => void) {
  const ref = React.useRef(onSuccess);
  ref.current = onSuccess;

  React.useEffect(() => {
    const handler = () => ref.current();
    window.addEventListener(MODAL_SUCCESS_EVENT, handler);
    return () => window.removeEventListener(MODAL_SUCCESS_EVENT, handler);
  }, []);
}

/**
 * Global modal provider that renders create modals at the app level.
 * Modals handle their own API calls so they work from any page
 * without requiring page-specific role checks or state.
 */
export const ModalProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [showCreateUser, setShowCreateUser] = useState(false);
  const [showInviteUser, setShowInviteUser] = useState(false);
  const [inviteUserDefaultRole, setInviteUserDefaultRole] = useState<'athlete' | 'coach' | undefined>(undefined);
  const [showCreateOrg, setShowCreateOrg] = useState(false);
  const [showCreateTeam, setShowCreateTeam] = useState(false);
  const [teamCreatedData, setTeamCreatedData] = useState<TeamCreatedData | null>(null);
  const [organizations, setOrganizations] = useState<{ id: number; name: string }[]>([]);

  const notifySuccess = () => window.dispatchEvent(new Event(MODAL_SUCCESS_EVENT));

  const openCreateUserModal = useCallback(() => setShowCreateUser(true), []);
  const openInviteUserModal = useCallback((options?: InviteModalOptions) => {
    setInviteUserDefaultRole(options?.defaultRole);
    setShowInviteUser(true);
  }, []);
  const openCreateOrgModal = useCallback(() => setShowCreateOrg(true), []);

  const openCreateTeamModal = useCallback(() => {
    const token = localStorage.getItem('access');
    if (!token) return;
    fetch(getApiUrl('/api/teams/organizations/'), {
      headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' }
    })
      .then(res => res.json())
      .then(data => {
        const list = Array.isArray(data) ? data : (data.results ?? data.data ?? []);
        setOrganizations(list.map((o: { id: number; name: string }) => ({ id: o.id, name: o.name })));
      })
      .catch(err => console.error('ModalProvider: fetch organizations', err));
    setShowCreateTeam(true);
  }, []);

  const handleCreateUser = async (userData: CreateUserData): Promise<boolean> => {
    const token = localStorage.getItem('access');
    if (!token) throw new Error('Authentication required. Please log in again.');
    const response = await fetch(getApiUrl('/api/teams/admin/create-user/'), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
      body: JSON.stringify(userData),
    });
    if (!response.ok) {
      let message = `Failed to create user (HTTP ${response.status})`;
      try {
        const ct = response.headers.get('content-type') || '';
        if (ct.includes('application/json')) {
          const errorData = await response.json();
          message = errorData.error || errorData.detail || errorData.message || message;
        }
      } catch { /* keep default message */ }
      throw new Error(message);
    }
    notifySuccess();
    return true;
  };

  const handleCreateOrganization = async (orgData: { name: string; description: string }): Promise<boolean> => {
    const token = localStorage.getItem('access');
    if (!token) throw new Error('Authentication required. Please log in again.');
    const response = await fetch(getApiUrl('/api/teams/admin/create-organization/'), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
      body: JSON.stringify(orgData),
    });
    if (!response.ok) {
      const err = await response.json().catch(() => ({}));
      throw new Error(err.error || err.detail || `HTTP ${response.status}`);
    }
    notifySuccess();
    return true;
  };

  const handleCreateTeam = async (teamData: { organization_id: number; sport: string; gender: 'mixed' | 'male' | 'female' }): Promise<boolean> => {
    const token = localStorage.getItem('access');
    if (!token) throw new Error('Authentication required. Please log in again.');
    const response = await fetch(getApiUrl('/api/teams/admin/create-team/'), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
      body: JSON.stringify(teamData),
    });
    if (!response.ok) {
      const err = await response.json().catch(() => ({}));
      throw new Error(err.error || err.detail || `HTTP ${response.status}`);
    }
    const result = await response.json();
    setTeamCreatedData(result);
    notifySuccess();
    return true;
  };

  return (
    <ModalContext.Provider value={{ openCreateUserModal, openInviteUserModal, openCreateOrgModal, openCreateTeamModal }}>
      {children}

      {showCreateUser && (
        <CreateUserModal
          onClose={() => setShowCreateUser(false)}
          onCreate={handleCreateUser}
        />
      )}

      {showInviteUser && (
        <InviteUserModal
          onClose={() => {
            setShowInviteUser(false);
            setInviteUserDefaultRole(undefined);
          }}
          defaultRole={inviteUserDefaultRole}
          onSuccess={notifySuccess}
        />
      )}

      {showCreateOrg && (
        <CreateOrganizationModal
          onClose={() => setShowCreateOrg(false)}
          onCreate={handleCreateOrganization}
        />
      )}

      {showCreateTeam && (
        <CreateTeamModal
          organizations={organizations}
          onClose={() => setShowCreateTeam(false)}
          onCreate={handleCreateTeam}
        />
      )}

      {teamCreatedData && (
        <TeamCreatedModal
          onClose={() => setTeamCreatedData(null)}
          teamData={{
            team: {
              id: teamCreatedData.team.id,
              name: teamCreatedData.team.name,
              organization: teamCreatedData.team.organization.name,
              sport: teamCreatedData.team.description || '',
              gender: 'mixed'
            },
            coach_code: teamCreatedData.coach_code,
            athlete_code: teamCreatedData.athlete_code
          }}
        />
      )}
    </ModalContext.Provider>
  );
};
